import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Car, Newspaper, Search, Menu, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useCompare } from '@/contexts/CompareContext';

interface MobileTabBarProps {
  moreOpen: boolean;
  onToggleMore: () => void;
}

/**
 * Fast bundnavigation til mobil. Fem punkter inden for tummelængde.
 * Skjules fra lg og op, hvor den almindelige header-navigation tager over.
 */
export function MobileTabBar({ moreOpen, onToggleMore }: MobileTabBarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedCars } = useCompare();

  const isActive = (href: string) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  const tabClass = (active: boolean) =>
    `flex flex-1 flex-col items-center justify-center gap-0.5 min-h-[52px] px-1 pt-1.5 pb-1 text-[11px] font-medium transition-colors ${
      active ? 'text-accent' : 'text-muted-foreground hover:text-foreground'
    }`;

  const handleSearch = () => {
    if (location.pathname.startsWith('/biler')) {
      const input = document.querySelector<HTMLInputElement>('input[type="search"], input[placeholder*="Søg"]');
      if (input) {
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        input.focus();
        return;
      }
    }
    navigate('/biler');
  };

  return (
    <nav
      aria-label="Mobilnavigation"
      className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur border-t border-border shadow-[0_-2px_12px_hsl(var(--foreground)/0.06)] pb-[env(safe-area-inset-bottom)]"
    >
      <div className="flex items-stretch">
        <Link to="/" className={tabClass(isActive('/'))} aria-current={isActive('/') ? 'page' : undefined}>
          <Home className="w-5 h-5" />
          Forside
        </Link>
        <Link to="/biler" className={tabClass(isActive('/biler'))} aria-current={isActive('/biler') ? 'page' : undefined}>
          <span className="relative">
            <Car className="w-5 h-5" />
            {selectedCars.length > 0 && (
              <Badge
                variant="default"
                className="absolute -top-2 -right-3 h-4 min-w-4 px-1 text-[10px] leading-none"
              >
                {selectedCars.length}
              </Badge>
            )}
          </span>
          Biler
        </Link>
        <Link to="/nyheder" className={tabClass(isActive('/nyheder'))} aria-current={isActive('/nyheder') ? 'page' : undefined}>
          <Newspaper className="w-5 h-5" />
          Nyheder
        </Link>
        <button type="button" onClick={handleSearch} className={tabClass(false)} aria-label="Søg elbiler">
          <Search className="w-5 h-5" />
          Søg
        </button>
        <button
          type="button"
          onClick={onToggleMore}
          className={tabClass(moreOpen)}
          aria-expanded={moreOpen}
          aria-label={moreOpen ? 'Luk menu' : 'Åbn menu'}
        >
          {moreOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          Mere
        </button>
      </div>
    </nav>
  );
}
